import { useEffect, useMemo, useState } from "react";
import { useParams, Link, useSearchParams } from "react-router-dom";
import { api, formatApiErrorDetail } from "../api";
import { useAuth } from "../AuthContext";
import { useI18n } from "../I18nContext";
import { expertiseLabel } from "../expertise";
import Metrics from "./Metrics";
import Roadmap from "./Roadmap";
import Framework from "./Framework";
import Documents from "./Documents";
import PortfolioNotes from "./PortfolioNotes";

const ROLE_SINGULAR = {
  entrepreneur: "founder",
  advisor: "advisor",
  provider: "provider",
  investor: "fundManager",
  accelerator: "accelerator",
};

function hostPath(url) {
  return (url || "").replace(/^https?:\/\/(www\.)?/, "").replace(/\/$/, "");
}

function sessionHref(s) {
  return s.type === "internal_1_1" ? `/app/team-1-1/${s.id}` : `/app/sessions/${s.id}`;
}

function FounderMeetings({ founderId, lang, t }) {
  const [sessions, setSessions] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    setErr("");
    setSessions(null);
    api.get("/sessions", { params: { founder_id: founderId } })
      .then((r) => setSessions(r.data || []))
      .catch((e) => {
        setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message);
        setSessions([]);
      });
  }, [founderId]);

  const { upcoming, past } = useMemo(() => {
    const now = Date.now();
    const items = (sessions || [])
      .map((s) => ({ s, at: new Date(s.scheduled_at).getTime() }))
      .sort((a, b) => a.at - b.at);
    return {
      upcoming: items.filter((x) => x.at > now).map((x) => x.s),
      past: items.filter((x) => x.at <= now).reverse().map((x) => x.s),
    };
  }, [sessions]);

  if (!sessions) return <div className="tb-loading">{t("common.loading")}</div>;

  const card = (s) => {
    const dt = new Date(s.scheduled_at);
    const dateStr = dt.toLocaleString(lang === "fr" ? "fr-CA" : "en-US", {
      weekday: "short",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
    const names = s.type === "internal_1_1"
      ? (s.participants || []).map((p) => p.name).filter(Boolean)
      : [s.advisor?.name, s.entrepreneur?.name].filter(Boolean);
    const who = [...new Set(names)].join(" · ");
    return (
      <Link key={s.id} to={sessionHref(s)} state={{ from: `/app/u/${founderId}?tab=meetings` }} className="tb-app-card" data-testid={`profile-meeting-${s.id}`}>
        <span className="tb-when">{dateStr}</span>
        <strong>{s.title || t("team1v1.badge.advisory")}</strong>
        {who && <small>{who}</small>}
      </Link>
    );
  };

  return (
    <div data-testid="profile-meetings">
      <p className="tb-muted" style={{ marginBottom: "1.5rem", maxWidth: 760 }}>
        {t("community.meetingsSubtitle")}
      </p>
      {err && <div className="tb-form-error" style={{ marginBottom: 12 }}>{err}</div>}
      <h2 className="tb-h2" style={{ marginBottom: 8 }}>{t("dash.upcoming")}</h2>
      {upcoming.length === 0 ? (
        <p className="tb-muted" data-testid="profile-meetings-no-upcoming">{t("dash.noUpcoming")}</p>
      ) : (
        <div className="tb-card-grid" data-testid="profile-meetings-upcoming">{upcoming.map(card)}</div>
      )}
      <h2 className="tb-h2" style={{ marginTop: "1.5rem", marginBottom: 8 }}>{t("dash.past")}</h2>
      {past.length === 0 ? (
        <p className="tb-muted" data-testid="profile-meetings-no-past">{t("dash.noPast")}</p>
      ) : (
        <div className="tb-card-grid" data-testid="profile-meetings-past">{past.map(card)}</div>
      )}
    </div>
  );
}

function ProfileMandates({ founderId, t }) {
  const [items, setItems] = useState(null);

  useEffect(() => {
    api.get("/mandates").then((r) => {
      const rows = (r.data || []).filter((m) => m.founder_id === founderId || m.founder?.id === founderId);
      setItems(rows);
    }).catch(() => setItems([]));
  }, [founderId]);

  if (items === null) return <div className="tb-loading">{t("common.loading")}</div>;
  if (items.length === 0) return <p className="tb-muted" data-testid="profile-mandates-empty">{t("dash.noMandates")}</p>;
  return (
    <div className="tb-card-grid" data-testid="profile-mandates">
      {items.map((m) => (
        <Link key={m.id} to={`/app/mandates/${m.id}`} className="tb-app-card" style={{ textDecoration: "none", color: "inherit" }}>
          <strong>{m.title}</strong>
          <small>{[m.advisor?.name, m.founder?.company].filter(Boolean).join(" · ")}</small>
          <small>{t("mandates.startDate")}: {m.start_date || "—"} · {t("mandates.endDate")}: {m.end_date || "—"}</small>
          <span className="tb-badge">{t(`mandates.status.${m.status}`)}</span>
        </Link>
      ))}
    </div>
  );
}

export default function UserProfile() {
  const { uid } = useParams();
  const [params] = useSearchParams();
  const { t, lang } = useI18n();
  const { user } = useAuth();
  const [profile, setProfile] = useState(null);
  const [err, setErr] = useState("");
  const [tab, setTab] = useState(() => params.get("tab") || "metrics");
  const [connecting, setConnecting] = useState(false);
  const [recommending, setRecommending] = useState(false);

  const load = () => {
    setErr("");
    api.get(`/users/${uid}/profile`, { params: { lang } }).then((r) => setProfile(r.data)).catch((e) => setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message));
  };
  useEffect(() => { load(); }, [uid, lang]);  // eslint-disable-line react-hooks/exhaustive-deps

  if (!profile) {
    return (
      <div className="tb-page">
        {err ? <div className="tb-form-error">{err}</div> : <div className="tb-loading">{t("common.loading")}</div>}
      </div>
    );
  }

  const connect = async () => {
    setConnecting(true);
    try { await api.post("/connections", { other_id: uid }); load(); }
    catch (e) { setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
    finally { setConnecting(false); }
  };

  const disconnect = async () => {
    if (!profile.connection_id) return;
    if (!window.confirm(t("community.confirmDisconnect"))) return;
    setConnecting(true);
    try { await api.delete(`/connections/${profile.connection_id}`); load(); }
    catch (e) { setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
    finally { setConnecting(false); }
  };

  const toggleRecommend = async () => {
    setRecommending(true);
    setErr("");
    try {
      if (profile.recommended) await api.delete(`/users/${uid}/recommend`);
      else await api.post(`/users/${uid}/recommend`);
      load();
    } catch (e) {
      setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setRecommending(false);
    }
  };

  const isSelf = Boolean(profile.is_self) || (user?.id && String(user.id) === String(uid));
  const isFounder = profile.role === "entrepreneur";
  const canSeeData = isSelf || profile.can_view_shared || user?.role === "admin";
  const canSeeDocs = canSeeData || Boolean(profile.can_view_documents) || Boolean(profile.connected);

  const recCount = profile.recommendation_count || 0;
  const recLabel = recCount === 0
    ? t("community.recommendCountZero")
    : recCount === 1
      ? t("community.recommendCountOne")
      : t("community.recommendCount", { n: recCount });

  return (
    <div className="tb-page">
      <div style={{ marginBottom: "1rem" }}>
        <Link to="/app/community" className="tb-back-link" data-testid="back-to-community">← {t("community.backTo")}</Link>
      </div>

      <div className="tb-block tb-bio-profile" style={{ marginBottom: "1.5rem" }}>
        <div style={{ display: "flex", alignItems: "flex-start", gap: 20, flexWrap: "wrap" }}>
          {profile.photo_url ? (
            <img src={profile.photo_url} alt={profile.name} className="tb-bio-profile-photo" />
          ) : (
            <div className="tb-bio-profile-photo tb-bio-profile-photo-fallback">
              {(profile.name || "?").slice(0, 1).toUpperCase()}
            </div>
          )}
          <div style={{ flex: 1, minWidth: 220 }}>
            <h1 className="tb-h1" data-testid="profile-name" style={{ margin: 0 }}>{profile.name}</h1>
            {profile.tagline && <div className="tb-bio-tagline" data-testid="profile-tagline">{profile.tagline}</div>}
            <div className="tb-muted" style={{ marginTop: 4 }} data-testid="profile-role">
              {t(`roles.${ROLE_SINGULAR[profile.role] || "members"}`)}
              {profile.company ? ` · ${profile.company}` : ""}
            </div>
            {profile.linkedin_url && (
              <a
                href={profile.linkedin_url}
                target="_blank"
                rel="noopener noreferrer"
                className="tb-bio-link"
                style={{ display: "inline-block", marginTop: 8 }}
                data-testid="profile-linkedin"
              >
                {t("community.linkedin")}
              </a>
            )}
            {profile.role === "advisor" && (
              <div className="tb-bio-recommend-count" data-testid="profile-recommend-count">
                {recLabel}
              </div>
            )}
            {(profile.expertise_areas || []).length > 0 && (
              <div className="tb-expertise-tags" style={{ marginTop: 10 }} data-testid="profile-expertises">
                {profile.expertise_areas.map((key) => (
                  <span key={key} className={`tb-expertise-tag ${key}`}>{expertiseLabel(t, key)}</span>
                ))}
              </div>
            )}
          </div>
          {!isSelf && (
            <div style={{ display: "flex", flexDirection: "column", gap: 8, alignItems: "flex-end" }}>
              {profile.connected ? (
                <>
                  <span className="tb-badge good" data-testid="connected-badge">✓ {t("community.connected")}</span>
                  {profile.connection_id && (
                    <button
                      className="tb-ghost-btn"
                      style={{ padding: "6px 12px", fontSize: "0.82rem", color: "var(--danger, #d97757)" }}
                      onClick={disconnect}
                      disabled={connecting}
                      data-testid="profile-disconnect"
                    >
                      {connecting ? "…" : t("community.disconnect")}
                    </button>
                  )}
                </>
              ) : (
                <button className="tb-primary-btn" onClick={connect} disabled={connecting} data-testid="profile-connect">
                  {connecting ? "…" : t("community.connect")}
                </button>
              )}
              {profile.role === "advisor" && (
                <button
                  className={profile.recommended ? "tb-ghost-btn" : "tb-primary-btn"}
                  onClick={toggleRecommend}
                  disabled={recommending}
                  data-testid="profile-recommend"
                >
                  {recommending ? "…" : profile.recommended ? t("community.recommended") : t("community.recommend")}
                </button>
              )}
            </div>
          )}
          {isSelf && (
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              <Link to="/app/settings" className="tb-ghost-btn" style={{ textDecoration: "none", padding: "8px 14px", whiteSpace: "nowrap" }} data-testid="profile-open-settings">
                {t("settings.title")} →
              </Link>
              {!isFounder && (
                <Link to="/app/portfolio" className="tb-ghost-btn" style={{ textDecoration: "none", padding: "8px 14px", whiteSpace: "nowrap" }} data-testid="profile-go-portfolio">
                  {t("portfolio.title")} →
                </Link>
              )}
              {profile.role === "provider" && (
                <Link to="/app/solutions/edit" className="tb-ghost-btn" style={{ textDecoration: "none", padding: "8px 14px", whiteSpace: "nowrap" }} data-testid="profile-edit-solution">
                  {t("solutions.editTitle")} →
                </Link>
              )}
            </div>
          )}
        </div>

        {profile.headline && (
          <div className="tb-bio-section" data-testid="profile-properties">
            <h3>{t("settings.headline")}</h3>
            <p data-testid="profile-headline">{profile.headline}</p>
          </div>
        )}

        {profile.bio && (
          <div className="tb-bio-section" data-testid="profile-bio">
            <h3>{t("settings.bio")}</h3>
            <p style={{ whiteSpace: "pre-line" }}>{profile.bio}</p>
          </div>
        )}
        {Object.keys(profile.expertise_details || {}).length > 0 && (
          <div className="tb-bio-section" data-testid="profile-expertise-details">
            <h3>{t("community.expertiseDetails")}</h3>
            <div className="tb-bio-expertise-list">
              {(profile.expertise_areas || Object.keys(profile.expertise_details)).map((key) => {
                const items = profile.expertise_details[key];
                if (!items?.length) return null;
                return (
                  <div key={key} className="tb-bio-expertise-group">
                    <span className={`tb-expertise-tag ${key}`}>{expertiseLabel(t, key)}</span>
                    <ul className="tb-bio-expertise-sub">
                      {items.map((item) => <li key={item}>{item}</li>)}
                    </ul>
                  </div>
                );
              })}
            </div>
          </div>
        )}
        {(profile.linkedin_url || profile.website_url || (profile.extra_links || []).length > 0) && (
          <div className="tb-bio-section" data-testid="profile-links">
            <h3>{t("community.links")}</h3>
            <ul className="tb-bio-links">
              {profile.linkedin_url && (
                <li>
                  <a href={profile.linkedin_url} target="_blank" rel="noopener noreferrer" className="tb-bio-link" data-testid="profile-linkedin-url">
                    {t("settings.linkedin")} · {hostPath(profile.linkedin_url)}
                  </a>
                </li>
              )}
              {profile.website_url && (
                <li>
                  <a href={profile.website_url} target="_blank" rel="noopener noreferrer" className="tb-bio-link" data-testid="profile-website">
                    {t("settings.website")} · {hostPath(profile.website_url)}
                  </a>
                </li>
              )}
              {(profile.extra_links || []).map((link) => (
                <li key={link.url}>
                  <a href={link.url} target="_blank" rel="noopener noreferrer" className="tb-bio-link">
                    {link.label || hostPath(link.url)}
                  </a>
                </li>
              ))}
            </ul>
          </div>
        )}
        {profile.solution_slug && (
          <div className="tb-bio-section">
            <Link to={`/app/solutions/${profile.solution_slug}`} className="tb-primary-btn" style={{ display: "inline-block", textDecoration: "none" }} data-testid="profile-solution">
              {t("solutions.viewOffering")}
            </Link>
          </div>
        )}
      </div>

      {isFounder && (
        <div className="tb-block">
          {!canSeeDocs ? (
            <p className="tb-muted" data-testid="profile-locked">{t("community.lockedHint")}</p>
          ) : (
            <>
              <div className="tb-tabs" data-testid="profile-data-tabs" style={{ display: "flex", gap: 6, marginBottom: "1.2rem", flexWrap: "wrap" }}>
                {canSeeData && <button className={`tb-chip ${tab === "metrics" ? "active" : ""}`} onClick={() => setTab("metrics")} data-testid="tab-metrics">{t("metrics.title")}</button>}
                {canSeeData && <button className={`tb-chip ${tab === "roadmap" ? "active" : ""}`} onClick={() => setTab("roadmap")} data-testid="tab-roadmap">{t("roadmap.title")}</button>}
                {canSeeData && <button className={`tb-chip ${tab === "framework" ? "active" : ""}`} onClick={() => setTab("framework")} data-testid="tab-framework">{t("framework.title")}</button>}
                <button className={`tb-chip ${(tab === "documents" || !canSeeData) ? "active" : ""}`} onClick={() => setTab("documents")} data-testid="tab-documents">{t("documents.title")}</button>
                {canSeeData && <button className={`tb-chip ${tab === "meetings" ? "active" : ""}`} onClick={() => setTab("meetings")} data-testid="tab-meetings">{t("community.meetings")}</button>}
                {canSeeData && <button className={`tb-chip ${tab === "notes" ? "active" : ""}`} onClick={() => setTab("notes")} data-testid="tab-notes">{t("portfolio.notes")}</button>}
                {canSeeData && <button className={`tb-chip ${tab === "mandates" ? "active" : ""}`} onClick={() => setTab("mandates")} data-testid="tab-mandates">{t("nav.mandates")}</button>}
              </div>
              {canSeeData && tab === "metrics" && <Metrics targetUserId={uid} hideHeader snapshotInline />}
              {canSeeData && tab === "roadmap" && <Roadmap targetUserId={uid} hideHeader snapshotInline />}
              {canSeeData && tab === "framework" && <Framework targetUserId={uid} hideHeader />}
              {(tab === "documents" || !canSeeData) && <Documents targetUserId={uid} hideHeader />}
              {canSeeData && tab === "meetings" && <FounderMeetings founderId={uid} lang={lang} t={t} />}
              {canSeeData && tab === "notes" && <PortfolioNotes founderId={uid} />}
              {canSeeData && tab === "mandates" && <ProfileMandates founderId={uid} t={t} />}
            </>
          )}
        </div>
      )}

      {err && <div className="tb-form-error" style={{ marginTop: 16 }}>{err}</div>}
    </div>
  );
}
